import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, CircularProgress, Typography, makeStyles } from '@material-ui/core'
import SydActionMenu from '../../../../commonDesign/SydActionMenu'
import SydButton from '../../../../commonDesign/Button'
import OperationalTable from '../../../../organisms/OperationalTable'
import EmptySection from '../../../../atoms/EmptySection'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import { useCheckPolicy } from '../../../../../hooks'
import { useClientDetails } from '../ClientDetailsFormContext'
import { useListHouseholdRegistrations } from '../../../../../api/householdRegistrations'
import EditAssignmentModal from './EditAssignmentModal'
import CreateOwnerGroupModal from './CreateOwnerGroupModal'

const rowPropType = PropTypes.shape({
  original: PropTypes.shape({
    accountId: PropTypes.number,
    accountName: PropTypes.string,
    accountNumber: PropTypes.string,
    orionRegistrationId: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    ownerGroupId: PropTypes.number,
    ownerGroupName: PropTypes.string,
    weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired
}).isRequired

const useStyles = makeStyles((theme) => ({
  panel: {
    padding: '1rem'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.75rem'
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: '2rem'
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: 'var(--color-text-tertiary)'
  },
  unassigned: {
    color: 'var(--color-text-tertiary)',
    fontStyle: 'italic'
  },
  weightOver100: {
    color: 'var(--color-warning, #f57c00)'
  }
}))

const formatPercent = (weight) => {
  if (weight == null) return '—'
  const n = Number(weight)
  if (!Number.isFinite(n)) return '—'
  return `${(n * 100).toFixed(2).replace(/\.?0+$/, '')}%`
}

const AccountNameCell = ({ row }) => row.original.accountName || '—'
AccountNameCell.propTypes = { row: rowPropType }

const OwnerGroupCell = ({ row, unassignedClass }) => {
  const reg = row.original
  return reg.ownerGroupName
    ? reg.ownerGroupName
    : <span className={unassignedClass}>—</span>
}
OwnerGroupCell.propTypes = {
  row: rowPropType,
  unassignedClass: PropTypes.string
}

const WeightCell = ({ row, overOneClass }) => {
  const reg = row.original
  const n = reg.weight == null ? null : Number(reg.weight)
  const overOne = n != null && n > 1
  return (
    <span className={overOne ? overOneClass : undefined}>
      {formatPercent(reg.weight)}
    </span>
  )
}
WeightCell.propTypes = {
  row: rowPropType,
  overOneClass: PropTypes.string
}

const ActionsCell = ({ row, buildActions }) => {
  const reg = row.original
  return <SydActionMenu actions={buildActions(reg)} subject={reg} />
}
ActionsCell.propTypes = {
  row: rowPropType,
  buildActions: PropTypes.func.isRequired
}

/**
 * Registrations tab on the Household detail page (SMT-994).
 *
 * Read-side: list-household-registrations returns one row per
 *   (account, registration, owner-assignment). Multi-owner registrations
 *   produce multiple rows; registrations without an owner produce a row
 *   with null ownerGroupId/ownerGroupName/weight ("—" rendered).
 *
 * Write-side: per-row action menu offers Edit (modify weight or remove) and
 *   Add Another (create another owner-group assignment for the same
 *   registration). Top-level "Create Owner Group" button creates a
 *   firm-level owner group without immediately assigning it.
 *
 * Permission gating:
 *   - Tab visibility: admin_view_accts (parent ClientDetailTabs).
 *   - Write affordances: admin_edit_owner_groups via useCheckPolicy.
 */
function RegistrationsTab () {
  const classes = useStyles()
  const { client } = useClientDetails()
  const clientId = client?.clientId

  const canEdit = useCheckPolicy('admin_edit_owner_groups')

  const { data, isLoading, isFetching } = useListHouseholdRegistrations(clientId)
  const rows = data?.data ?? []

  const [editModal, setEditModal] = useState({ open: false, mode: 'edit', row: null })
  const [createOpen, setCreateOpen] = useState(false)

  const closeEdit = useCallback(() => setEditModal({ open: false, mode: 'edit', row: null }), [])

  const buildActions = useCallback((row) => {
    const items = []
    if (row.ownerGroupId) {
      items.push({
        key: 'edit-weight',
        label: 'Edit weight…',
        onClick: () => setEditModal({ open: true, mode: 'edit', row })
      })
      items.push({
        key: 'add-another',
        label: 'Add another owner group…',
        onClick: () => setEditModal({ open: true, mode: 'assign', row })
      })
    } else {
      items.push({
        key: 'assign-owner',
        label: 'Assign owner group…',
        onClick: () => setEditModal({ open: true, mode: 'assign', row })
      })
    }
    return items
  }, [])

  const columns = useMemo(() => {
    const renderOwnerGroup = (props) => (
      <OwnerGroupCell {...props} unassignedClass={classes.unassigned} />
    )
    const renderWeight = (props) => (
      <WeightCell {...props} overOneClass={classes.weightOver100} />
    )
    const renderActions = (props) => (
      <ActionsCell {...props} buildActions={buildActions} />
    )

    const baseColumns = [
      {
        Header: 'Account Name',
        accessor: 'accountName',
        id: 'accountName',
        Cell: AccountNameCell
      },
      {
        Header: 'Account #',
        accessor: 'accountNumber',
        id: 'accountNumber'
      },
      {
        Header: 'Orion Registration ID',
        accessor: 'orionRegistrationId',
        id: 'orionRegistrationId'
      },
      {
        Header: 'First Name',
        accessor: 'firstName',
        id: 'firstName'
      },
      {
        Header: 'Last Name',
        accessor: 'lastName',
        id: 'lastName'
      },
      {
        Header: 'Owner Group',
        accessor: 'ownerGroupName',
        id: 'ownerGroupName',
        Cell: renderOwnerGroup
      },
      {
        Header: 'Weight',
        accessor: 'weight',
        id: 'weight',
        Cell: renderWeight
      }
    ]

    if (canEdit) {
      baseColumns.push({
        Header: '',
        id: 'actions',
        disableSortBy: true,
        Cell: renderActions
      })
    }

    return baseColumns
  }, [classes.unassigned, classes.weightOver100, canEdit, buildActions])

  if (!clientId) return null

  if (isLoading) {
    return (
      <div className={classes.panel}>
        <div className={classes.loading}><CircularProgress /></div>
      </div>
    )
  }

  return (
    <FadeIn>
      <div className={classes.panel}>
        <div className={classes.header}>
          <Typography variant='h6'>Registrations &amp; Owner-Group Assignments</Typography>
          {canEdit ? (
            <SydButton variant='outline' onClick={() => setCreateOpen(true)}>
              + Create owner group
            </SydButton>
          ) : null}
        </div>

        {rows.length === 0 ? (
          <EmptySection
            title='No registrations to manage'
            description='This household has no open accounts with linked registrations yet.'
          />
        ) : (
          <Box>
            <OperationalTable.Wrapper>
              <OperationalTable
                mode='client'
                columns={columns}
                data={rows}
                defaultSort={[]}
                total={rows.length}
                hideFooter
                loading={isFetching}
              />
            </OperationalTable.Wrapper>
          </Box>
        )}

        {clientId ? (
          <EditAssignmentModal
            open={editModal.open}
            onClose={closeEdit}
            mode={editModal.mode}
            clientId={clientId}
            row={editModal.row}
          />
        ) : null}

        {clientId ? (
          <CreateOwnerGroupModal
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            clientId={clientId}
          />
        ) : null}
      </div>
    </FadeIn>
  )
}

export default RegistrationsTab
